import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, Filter, Plane, Eye, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import PageHeader from '../components/PageHeader';
import PageLoader from '../components/PageLoader';
import EmptyState from '../components/EmptyState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Statuses the agent can set manually
const AGENT_STATUSES = [
  { value: "pending",              label: "Pending" },
  { value: "searching",            label: "Searching" },
  { value: "offers_found",         label: "Offers Found" },
  { value: "quoted",               label: "Quoted" },
  { value: "booked",               label: "Booked" },
  { value: "closed",               label: "Closed" },
  { value: "cancelled",            label: "Cancelled" },
  { value: "converted_to_booking", label: "Converted to Booking" },
];

export default function FlightRequests() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAuthHeaders } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(
    location.state?.statusFilter || "all"
  );
  const [updatingId, setUpdatingId] = useState(null);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const response = await axios.get(`${API}/flight-requests`, {
        headers: getAuthHeaders(),
        params
      });
      setRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch requests:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const handleStatusChange = async (requestId, newStatus) => {
    setUpdatingId(requestId);
    try {
      await axios.patch(
        `${API}/flight-requests/${requestId}`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      setRequests(prev =>
        prev.map(r => r.id === requestId ? { ...r, status: newStatus } : r)
      );
      toast.success("Status updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredRequests = requests.filter(request => {
    const searchLower = searchTerm.toLowerCase();
    return (
      request.first_name.toLowerCase().includes(searchLower) ||
      request.last_name.toLowerCase().includes(searchLower) ||
      request.email.toLowerCase().includes(searchLower) ||
      request.segments.some(seg =>
        seg.departure_airport.toLowerCase().includes(searchLower) ||
        seg.arrival_airport.toLowerCase().includes(searchLower)
      )
    );
  });

  const getStatusColor = (status) => {
    const colors = {
      pending:              "bg-amber-100 text-amber-800",
      searching:            "bg-blue-100 text-blue-800",
      offers_found:         "bg-emerald-100 text-emerald-800",
      quoted:               "bg-violet-100 text-violet-800",
      booked:               "bg-violet-100 text-violet-800",
      ticketed:             "bg-teal-100 text-teal-800",
      completed:            "bg-slate-100 text-slate-800",
      closed:               "bg-slate-100 text-slate-600",
      cancelled:            "bg-red-100 text-red-800",
      converted_to_booking: "bg-teal-100 text-teal-700",
    };
    return colors[status] || "bg-slate-100 text-slate-800";
  };

  const getTravelTypeLabel = (type) => {
    const labels = {
      one_way:    "One Way",
      return:     "Return",
      multi_city: "Multi City"
    };
    return labels[type] || type;
  };

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="flight-requests-page">
        {/* Header */}
        <PageHeader
          title="Flight Requests"
          subtitle="Manage and process customer flight requests"
          actions={
            <button
              onClick={fetchRequests}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
              title="Refresh"
              data-testid="refresh-requests-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          }
        />

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, email, or airport..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="searching">Searching</SelectItem>
                  <SelectItem value="offers_found">Offers Found</SelectItem>
                  <SelectItem value="quoted">Quoted</SelectItem>
                  <SelectItem value="booked">Booked</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="converted_to_booking">Converted to Booking</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card data-testid="requests-table-card">
          <CardContent className="p-0">
            {loading ? (
              <PageLoader inline message="Loading requests…" />
            ) : filteredRequests.length === 0 ? (
              <EmptyState
                title="No flight requests"
                description={
                  searchTerm || statusFilter !== "all"
                    ? "Try adjusting your filters"
                    : "Flight requests will appear here once submitted."
                }
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Request #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead>Passengers</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map((request) => (
                    <TableRow
                      key={request.id}
                      className="table-row-hover cursor-pointer"
                      onClick={() => navigate(`/requests/${request.id}`)}
                      data-testid={`request-row-${request.id}`}
                    >
                      <TableCell>
                        <span className="font-mono font-bold text-indigo-700">
                          {request.request_number || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">
                            {request.first_name} {request.last_name}
                          </p>
                          <p className="text-sm text-slate-500">{request.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 font-mono text-sm">
                          <span>{request.segments[0]?.departure_airport}</span>
                          <Plane className="w-3 h-3 text-slate-400" />
                          <span>{request.segments[0]?.arrival_airport}</span>
                          {request.segments.length > 1 && (
                            <span className="text-slate-400">
                              +{request.segments.length - 1}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="capitalize">{getTravelTypeLabel(request.travel_type)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="capitalize">{request.travel_class}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {request.passengers.adults}A
                          {request.passengers.children > 0 && ` ${request.passengers.children}C`}
                          {request.passengers.infants > 0 && ` ${request.passengers.infants}I`}
                        </span>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={request.status}
                          onValueChange={(val) => handleStatusChange(request.id, val)}
                          disabled={updatingId === request.id}
                        >
                          <SelectTrigger
                            className={`h-7 text-xs font-semibold border-0 shadow-none px-2 w-auto min-w-[110px] ${getStatusColor(request.status)}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {AGENT_STATUSES.map(({ value, label }) => (
                              <SelectItem key={value} value={value} className="text-xs">
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-500">
                          {format(new Date(request.created_at), "MMM d, yyyy")}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/requests/${request.id}`);
                          }}
                          data-testid={`view-request-${request.id}-btn`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
